/**
 * PURE option builder for the unified Pie & Composition family.
 *
 * No Angular / no ECharts-runtime dependency — takes capabilities + rows (or
 * pre-resolved nodes) and returns a plain EChartsOption, so all variant logic
 * is unit-testable. The component is just a lifecycle shell around
 * `buildPieCompositionTree` + `buildPieCompositionOption`.
 *
 * Two-step pipeline (unlike Scatter & Bubble, which builds the option in one
 * pass): the component builds a hierarchy ONCE from rows via
 * `buildPieCompositionTree`, then re-derives the option from whichever level
 * of that hierarchy the user has drilled into via `buildPieCompositionOption`
 * — no refetch on drill, just a pure re-slice of the already-built tree.
 *
 * Variant → behaviour (driven by config `variantFlags`, NOT name strings):
 *   pie / donut / semi-donut   → single ECharts `pie` series, radius/angle vary.
 *   nested-donut               → 2 `pie` series (parent ring + category ring).
 *   sunburst                   → native `sunburst` series, own click-drill (no
 *                                 native zoom — `nodeClick:false`).
 *   treemap                    → native `treemap` series, own click-drill (no
 *                                 native breadcrumb — `breadcrumb:{show:false}`).
 *   radial/circular/ring-progress → `gauge` series, one KPI meter per style.
 *
 * Multiple Categories (Standard/Progress only — inert for Hierarchy, which is
 * already multi-level via `parentField`): renders N small-multiple instances
 * of the base chart, one per top-level (parent) group.
 *
 * EVERY styling constant is read from `capabilities.chartDefaults` (sourced
 * from JSON `pieCompositionChartDefaults`). The FALLBACK object below is a
 * last-resort safety net ONLY (so the pure builder runs standalone in tests /
 * with no config); edit visuals in the JSON, never here.
 */
import type { EChartsOption } from 'echarts';
import { toCapitalCase } from '../../utils/text-case.utils';
import type {
  PcNode,
  PieCompositionCapabilities,
  PieCompositionGaugePreset,
} from './unified-pie-composition-chart.types';

type Row = Record<string, unknown>;

/** LAST-RESORT safety net ONLY — the authoritative values live in the JSON
 *  (`pieCompositionChartDefaults`). Used solely when both the per-tile
 *  capability AND the config are missing, so a broken/absent config never
 *  throws. */
const FALLBACK = {
  palette: ['#1f6feb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'],
  innerRadius: '40%',
  outerRadius: '70%',
  pieRadius: '70%',
  semiDonutAngles: { start: 180, end: 360 },
  // A half ring only occupies the upper half of its circle, so its centre sits
  // low in the box; at 50% it floated in the upper-middle with a blank band below.
  semiDonutCenter: ['50%', '72%'] as [string, string],
  nestedDonutBands: [['0%', '45%'], ['50%', '75%']] as Array<[string, string]>,
  progressPresets: {
    radial: { startAngle: 225, endAngle: -45, roundCap: false, showTicks: true, trackWidth: 14 },
    circular: { startAngle: 90, endAngle: -270, roundCap: true, showTicks: false, trackWidth: 18 },
    ring: { startAngle: 90, endAngle: -270, roundCap: false, showTicks: false, trackWidth: 8 },
  } as Record<string, PieCompositionGaugePreset>,
  trackColor: '#e2e8f0',
  labelFontSize: 11,
  labelColor: '#475569',
  gradient: ['#fde047', '#16a34a'],
  layout: { smallMultipleGap: 6, legendRightOffset: 4, gridTop: 12, gridBottom: 12 },
};

/** Numeric coercion tolerant of "1,234"/"$1.2k" → numeric part; non-numeric →
 *  null. */
function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value.replace(/[^0-9.eE+-]/g, ''));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }
  return null;
}

/** Lightweight number formatter (kept local so the builder has no Angular
 *  dep). */
function formatValue(value: number, capabilities: PieCompositionCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16) || 0;
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

/** Linear interpolation between two RGB tuples, t clamped to [0,1]. */
function lerpRgb(low: [number, number, number], high: [number, number, number], t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const r = Math.round(low[0] + (high[0] - low[0]) * clamped);
  const g = Math.round(low[1] + (high[1] - low[1]) * clamped);
  const b = Math.round(low[2] + (high[2] - low[2]) * clamped);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Linear color interpolation between two hex colors, t clamped to [0,1]. */
function lerpColor(low: string, high: string, t: number): string {
  return lerpRgb(hexToRgb(low), hexToRgb(high), t);
}

// ── Tree construction (rows → PcNode[]) ─────────────────────────────────────

function toPcNode(row: Row, categoryField: string, valueField: string, colorField: string | undefined, childrenField: string): PcNode {
  const childRows = Array.isArray(row[childrenField]) ? (row[childrenField] as Row[]) : undefined;
  const children = childRows?.map((r) => toPcNode(r, categoryField, valueField, colorField, childrenField));
  const name = String(row[categoryField] ?? row['name'] ?? '').trim() || '(blank)';
  const ownValue = toNum(row[valueField] ?? row['value']) ?? 0;
  const value = children?.length ? children.reduce((s, c) => s + c.value, 0) : ownValue;
  const measure = colorField ? toNum(row[colorField]) ?? undefined : undefined;
  return { name, value, children, measure, row };
}

/** Groups flat rows by `parentField`, SUMming `valueField` (and averaging
 *  `colorField`, when present) per (parent, category) leaf, then per parent.
 *  Blank/null parent values are grouped under '(blank)' rather than silently
 *  dropped. */
function nestRowsByParent(rows: Row[], parentField: string, categoryField: string, valueField: string, colorField?: string): PcNode[] {
  const parents = new Map<string, Map<string, { value: number; colorSum: number; colorCount: number; row: Row }>>();
  const parentOrder: string[] = [];
  rows.forEach((row) => {
    const parentRaw = row[parentField];
    const parentKey = parentRaw === undefined || parentRaw === null || String(parentRaw).trim() === '' ? '(blank)' : String(parentRaw).trim();
    const categoryRaw = row[categoryField];
    const categoryKey = categoryRaw === undefined || categoryRaw === null || String(categoryRaw).trim() === '' ? '(blank)' : String(categoryRaw).trim();
    const value = toNum(row[valueField]) ?? 0;
    if (!parents.has(parentKey)) { parents.set(parentKey, new Map()); parentOrder.push(parentKey); }
    const catMap = parents.get(parentKey) as Map<string, { value: number; colorSum: number; colorCount: number; row: Row }>;
    const existing = catMap.get(categoryKey);
    const colorVal = colorField ? toNum(row[colorField]) : null;
    if (existing) {
      existing.value += value;
      if (colorVal !== null) { existing.colorSum += colorVal; existing.colorCount += 1; }
    } else {
      catMap.set(categoryKey, { value, colorSum: colorVal ?? 0, colorCount: colorVal !== null ? 1 : 0, row });
    }
  });
  return parentOrder.map((parentName) => {
    const catMap = parents.get(parentName) as Map<string, { value: number; colorSum: number; colorCount: number; row: Row }>;
    const children: PcNode[] = Array.from(catMap.entries()).map(([name, agg]) => ({
      name,
      value: agg.value,
      measure: agg.colorCount ? agg.colorSum / agg.colorCount : undefined,
      row: agg.row,
    }));
    const total = children.reduce((s, c) => s + c.value, 0);
    const measures = children.map((c) => c.measure).filter((m): m is number => m !== undefined);
    return {
      name: parentName,
      value: total,
      children,
      measure: measures.length ? measures.reduce((a, b) => a + b, 0) / measures.length : undefined,
    };
  });
}

/** Groups flat rows by `categoryField` (SUM `valueField`, average
 *  `colorField`) — no hierarchy, the plain single-level case. */
function flatLeafNodes(rows: Row[], categoryField: string, valueField: string, colorField?: string): PcNode[] {
  const map = new Map<string, { value: number; colorSum: number; colorCount: number; row: Row }>();
  const order: string[] = [];
  rows.forEach((row) => {
    const raw = row[categoryField];
    const key = raw === undefined || raw === null || String(raw).trim() === '' ? '(blank)' : String(raw).trim();
    const value = toNum(row[valueField]) ?? 0;
    const colorVal = colorField ? toNum(row[colorField]) : null;
    if (!map.has(key)) { map.set(key, { value: 0, colorSum: 0, colorCount: 0, row }); order.push(key); }
    const bucket = map.get(key) as { value: number; colorSum: number; colorCount: number; row: Row };
    bucket.value += value;
    if (colorVal !== null) { bucket.colorSum += colorVal; bucket.colorCount += 1; }
  });
  return order.map((name) => {
    const b = map.get(name) as { value: number; colorSum: number; colorCount: number; row: Row };
    return { name, value: b.value, measure: b.colorCount ? b.colorSum / b.colorCount : undefined, row: b.row };
  });
}

/**
 * Build the hierarchy the option builder renders from. Auto-detects THREE
 * input shapes so both API-shaped and flat-table datasets work with zero
 * extra prep:
 *   1. Pre-nested rows carrying a `children` array (name via
 *      `capabilities.childrenField`, default 'children') — used as-is.
 *   2. Flat rows with `categoryField` + `parentField` — grouped/summed into a
 *      2-level tree client-side.
 *   3. Flat rows with just `categoryField` — a single flat level of leaves.
 */
export function buildPieCompositionTree(rows: Array<Record<string, unknown>>, capabilities: PieCompositionCapabilities): PcNode[] {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (!safeRows.length || !capabilities?.categoryField || !capabilities?.valueField) return [];
  const childrenField = capabilities.childrenField || 'children';
  const hasPreNested = safeRows.some((r) => Array.isArray((r as Row)[childrenField]));
  if (hasPreNested) {
    return safeRows.map((r) => toPcNode(r, capabilities.categoryField, capabilities.valueField, capabilities.colorField, childrenField));
  }
  if (capabilities.parentField) {
    return nestRowsByParent(safeRows, capabilities.parentField, capabilities.categoryField, capabilities.valueField, capabilities.colorField);
  }
  return flatLeafNodes(safeRows, capabilities.categoryField, capabilities.valueField, capabilities.colorField);
}

// ── Color resolution (categorical palette OR Color-by-Measure gradient) ────

function buildColorResolver(nodes: PcNode[], capabilities: PieCompositionCapabilities, palette: string[], gradient: [string, string]): (node: PcNode, index: number) => string {
  const colorByMeasure = capabilities.colorByMeasure && !!capabilities.colorField;
  if (!colorByMeasure) return (_node, i) => palette[i % palette.length];
  const measures = nodes.map((n) => n.measure ?? n.value);
  const lo = Math.min(...measures);
  const hi = Math.max(...measures);
  return (node) => {
    const m = node.measure ?? node.value;
    const t = hi === lo ? 0.5 : (m - lo) / (hi - lo);
    return lerpColor(gradient[0], gradient[1], t);
  };
}

// ── Sort / Top N ─────────────────────────────────────────────────────────

function applySortAndTopN(nodes: PcNode[], capabilities: PieCompositionCapabilities): PcNode[] {
  let list = [...nodes];
  const sortBy = capabilities.sortBy || 'value-desc';
  if (sortBy === 'value-desc') list.sort((a, b) => b.value - a.value);
  else if (sortBy === 'value-asc') list.sort((a, b) => a.value - b.value);
  else if (sortBy === 'name-asc') list.sort((a, b) => a.name.localeCompare(b.name));
  else if (sortBy === 'name-desc') list.sort((a, b) => b.name.localeCompare(a.name));
  const topN = capabilities.topN;
  if (topN && topN > 0 && list.length > topN) {
    const head = list.slice(0, topN);
    const rest = list.slice(topN);
    const otherValue = rest.reduce((s, n) => s + n.value, 0);
    head.push({ name: 'Other', value: otherValue });
    list = head;
  }
  return list;
}

// ── Legend / tooltip (shared across variants) ───────────────────────────────

/** Native ECharts legend: entries are plain names, `selected` left to ECharts'
 *  own default (every entry on), so a click just toggles that one entry's
 *  slice — the built-in inactive style (dimmed marker + text) renders a
 *  deselected entry and the remaining slices re-normalize to 100% on their
 *  own. Same native multi-toggle behavior as the Scatter & Bubble family's
 *  legend — no custom `legendselectchanged` handling needed. */
function buildLegend(names: string[], capabilities: PieCompositionCapabilities, layout: { legendRightOffset: number }): Record<string, unknown> | undefined {
  const legendPos = capabilities.legendPosition || 'bottom';
  const showLegend = names.length > 0 && capabilities.showLegend !== false && legendPos !== 'none';
  if (!showLegend) return undefined;
  return {
    data: names,
    type: 'scroll',
    ...(legendPos === 'top' ? { top: 0 } : legendPos === 'right' ? { right: layout.legendRightOffset, orient: 'vertical', top: 'middle' } : legendPos === 'left' ? { left: 0, orient: 'vertical', top: 'middle' } : { bottom: 0 }),
  };
}

function buildTooltip(capabilities: PieCompositionCapabilities, valueLabel: string): Record<string, unknown> {
  const tooltipTrigger = capabilities.tooltipTrigger || 'item';
  const tooltipOff = capabilities.tooltipEnabled === false || tooltipTrigger === 'none';
  if (tooltipOff) return { show: false };
  return {
    show: true,
    trigger: tooltipTrigger === 'auto' ? 'item' : tooltipTrigger,
    formatter: (params: unknown): string => {
      const p = (Array.isArray(params) ? params[0] : params) as { name?: string; value?: unknown; percent?: number; marker?: string; seriesName?: string };
      const marker = p?.marker ?? '';
      const name = p?.name ?? '';
      const value = typeof p?.value === 'number' ? formatValue(p.value, capabilities) : String(p?.value ?? '');
      const pct = p?.percent !== undefined ? ` (${p.percent}%)` : '';
      return `${marker}${name}<br/>${toCapitalCase(valueLabel)} : ${value}${pct}`;
    },
    ...(capabilities.tooltipBackground ? { backgroundColor: capabilities.tooltipBackground } : {}),
    ...(capabilities.tooltipBorderColor ? { borderColor: capabilities.tooltipBorderColor } : {}),
    ...(capabilities.tooltipBorderRadius !== undefined ? { borderRadius: capabilities.tooltipBorderRadius } : {}),
    textStyle: {
      ...(capabilities.tooltipTextColor ? { color: capabilities.tooltipTextColor } : {}),
      ...(capabilities.tooltipFontSize ? { fontSize: capabilities.tooltipFontSize } : {}),
    },
  };
}

function labelConfig(capabilities: PieCompositionCapabilities, labelFontSize: number, labelColor: string): Record<string, unknown> | undefined {
  if (capabilities.showValues === false) return { show: false };
  return {
    show: true,
    position: capabilities.labelPosition || 'outside',
    fontSize: labelFontSize,
    color: labelColor,
    formatter: '{b}: {d}%',
  };
}

// ── Small-multiples grid geometry ───────────────────────────────────────────

/** Evenly-spaced [x%, y%] centers + a shared per-cell radius for N chart
 *  instances laid out in as-square-as-possible a grid. */
function smallMultiplesGrid(count: number, gapPct: number): Array<{ center: [string, string]; radius: string }> {
  if (count <= 0) return [];
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  const cellW = 100 / cols;
  const cellH = 100 / rows;
  const radiusPct = Math.max(8, Math.min(cellW, cellH) / 2 - gapPct);
  const out: Array<{ center: [string, string]; radius: string }> = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = cellW * col + cellW / 2;
    const cy = cellH * row + cellH / 2;
    out.push({ center: [`${cx}%`, `${cy}%`], radius: `${radiusPct}%` });
  }
  return out;
}

// ── Pie / Donut / Semi Donut ─────────────────────────────────────────────

interface PieGeometry {
  isDonut: boolean;
  isSemiDonut: boolean;
  inner: string;
  outer: string;
  startAngle: number;
  endAngle?: number;
  /** Series centre for the single-chart case (semi donut sits low in the box). */
  center?: [string, string];
}

function resolvePieGeometry(capabilities: PieCompositionCapabilities, d: Record<string, unknown>, isDonut: boolean, isSemiDonut: boolean): PieGeometry {
  const inner = capabilities.innerRadius || (d['innerRadius'] as string) || FALLBACK.innerRadius;
  const outer = capabilities.outerRadius || (d['outerRadius'] as string) || FALLBACK.outerRadius;
  const semiAngles = (d['semiDonutAngles'] as { start?: number; end?: number }) || FALLBACK.semiDonutAngles;
  const startAngle = isSemiDonut ? capabilities.startAngle ?? semiAngles.start ?? FALLBACK.semiDonutAngles.start : capabilities.startAngle ?? 90;
  const endAngle = isSemiDonut ? capabilities.endAngle ?? semiAngles.end ?? FALLBACK.semiDonutAngles.end : undefined;
  const cfgCenter = d['semiDonutCenter'] as [string, string] | undefined;
  const center = isSemiDonut ? (Array.isArray(cfgCenter) && cfgCenter.length === 2 ? cfgCenter : FALLBACK.semiDonutCenter) : undefined;
  return { isDonut, isSemiDonut, inner, outer, startAngle, endAngle, center };
}

function buildPieSeries(nodes: PcNode[], colorFn: (node: PcNode, index: number) => string, geometry: PieGeometry, label: Record<string, unknown> | undefined, center: [string, string] | undefined = undefined, name = ''): Record<string, unknown> {
  const resolvedCenter: [string, string] = center ?? geometry.center ?? ['50%', '50%'];
  const radius = geometry.isDonut ? [geometry.inner, geometry.outer] : ['0%', FALLBACK.pieRadius];
  // Every slice is always in `data` — toggling a legend entry off (see
  // `buildLegend`) hides just that slice via ECharts' own native selection,
  // which also gets percentage re-normalization for free.
  const data = nodes.map((n, i) => ({ name: n.name, value: n.value, itemStyle: { color: colorFn(n, i) } }));
  return {
    name,
    type: 'pie',
    radius,
    center: resolvedCenter,
    startAngle: geometry.startAngle,
    ...(geometry.endAngle !== undefined ? { endAngle: geometry.endAngle } : {}),
    avoidLabelOverlap: true,
    itemStyle: { borderWidth: 1, borderColor: '#fff' },
    label: label ?? { show: true, formatter: '{b}: {d}%' },
    labelLine: { show: label?.['show'] !== false },
    data,
  };
}

// ── Nested Donut (2 rings) ──────────────────────────────────────────────

function buildNestedDonutSeries(nodes: PcNode[], capabilities: PieCompositionCapabilities, palette: string[], colorFn: (node: PcNode, index: number) => string, d: Record<string, unknown>, label: Record<string, unknown> | undefined): Record<string, unknown>[] {
  const bands = ((d['nestedDonutBands'] as Array<[string, string]>) || FALLBACK.nestedDonutBands);
  const innerBand = bands[0] || FALLBACK.nestedDonutBands[0];
  const outerBand = bands[1] || FALLBACK.nestedDonutBands[1];
  // The inner (Group) ring's data names match the legend 1:1, so toggling a
  // group is the legend's own native selection (see `buildLegend`) doing the
  // hide/show — no manual filtering needed here. The outer (Category) ring's
  // data is the *children's* names, which the legend never lists, so it
  // isn't affected by legend toggles (same as any other series ECharts'
  // legend can't address by name).
  const innerData = nodes.map((n, i) => ({ name: n.name, value: n.value, itemStyle: { color: colorFn(n, i) } }));
  const outerData: Array<{ name: string; value: number; itemStyle: { color: string } }> = [];
  nodes.forEach((parent, pi) => {
    const children = parent.children?.length ? parent.children : [{ name: parent.name, value: parent.value }];
    children.forEach((child, ci) => {
      const base = hexToRgb(palette[pi % palette.length]);
      const shadeT = children.length > 1 ? ci / Math.max(1, children.length - 1) : 0;
      const shaded = lerpRgb(base, [255, 255, 255], shadeT * 0.35);
      outerData.push({ name: child.name, value: child.value, itemStyle: { color: shaded } });
    });
  });
  return [
    {
      name: 'Group',
      type: 'pie',
      radius: innerBand,
      itemStyle: { borderWidth: 1, borderColor: '#fff' },
      label: { show: true, position: 'inside', formatter: '{b}' },
      data: innerData,
    },
    {
      name: 'Category',
      type: 'pie',
      radius: outerBand,
      itemStyle: { borderWidth: 1, borderColor: '#fff' },
      label: label ?? { show: true, formatter: '{b}: {d}%' },
      data: outerData,
    },
  ];
}

// ── Sunburst / Treemap ──────────────────────────────────────────────────

function toEChartsTreeNode(node: PcNode): Record<string, unknown> {
  return {
    name: node.name,
    value: node.value,
    ...(node.children?.length ? { children: node.children.map(toEChartsTreeNode) } : {}),
  };
}

function buildSunburstSeries(nodes: PcNode[], colorFn: (node: PcNode, index: number) => string): Record<string, unknown> {
  return {
    type: 'sunburst',
    radius: ['0%', '90%'],
    nodeClick: false,
    emphasis: { focus: 'ancestor' },
    label: { rotate: 'radial' },
    data: nodes.map((n, i) => ({ ...toEChartsTreeNode(n), itemStyle: { color: colorFn(n, i) } })),
  };
}

function buildTreemapSeries(nodes: PcNode[], colorFn: (node: PcNode, index: number) => string, capabilities: PieCompositionCapabilities): Record<string, unknown> {
  return {
    type: 'treemap',
    roam: false,
    nodeClick: false,
    breadcrumb: { show: false },
    label: { show: capabilities.showValues !== false, formatter: '{b}' },
    upperLabel: { show: true, height: 20 },
    data: nodes.map((n, i) => ({ ...toEChartsTreeNode(n), itemStyle: { color: colorFn(n, i) } })),
  };
}

// ── Progress (gauge) ─────────────────────────────────────────────────────

function resolveProgressPreset(capabilities: PieCompositionCapabilities, d: Record<string, unknown>, styleKey: 'radial' | 'circular' | 'ring'): Required<PieCompositionGaugePreset> {
  const cfgPresets = (d['progressPresets'] as Record<string, PieCompositionGaugePreset>) || {};
  const base = { ...FALLBACK.progressPresets[styleKey], ...(cfgPresets[styleKey] || {}) };
  return {
    startAngle: base.startAngle ?? 90,
    endAngle: base.endAngle ?? -270,
    roundCap: capabilities.roundCap ?? base.roundCap ?? false,
    showTicks: base.showTicks ?? false,
    trackWidth: capabilities.trackWidth ?? base.trackWidth ?? 14,
  };
}

function buildGaugeSeries(node: PcNode, capabilities: PieCompositionCapabilities, preset: Required<PieCompositionGaugePreset>, color: string, trackColor: string, center: [string, string], radius: string): Record<string, unknown> {
  const target = capabilities.targetValue ?? node.value;
  const max = target > 0 ? target : 100;
  return {
    type: 'gauge',
    center,
    radius,
    startAngle: preset.startAngle,
    endAngle: preset.endAngle,
    min: 0,
    max,
    progress: { show: true, width: preset.trackWidth, roundCap: preset.roundCap, itemStyle: { color } },
    pointer: { show: false },
    axisLine: { lineStyle: { width: preset.trackWidth, color: [[1, trackColor]] } },
    axisTick: { show: preset.showTicks },
    splitLine: { show: preset.showTicks, length: 8 },
    axisLabel: { show: preset.showTicks, fontSize: 9 },
    anchor: { show: false },
    title: { show: true, offsetCenter: [0, '70%'], fontSize: 12 },
    detail: {
      show: capabilities.showValues !== false,
      valueAnimation: true,
      offsetCenter: [0, 0],
      formatter: (value: number) => formatValue(value, capabilities),
      fontSize: 18,
      fontWeight: 600,
    },
    data: [{ value: node.value, name: node.name }],
  };
}

// ── Main entry point ─────────────────────────────────────────────────────

/**
 * Build the EChartsOption for a unified Pie & Composition chart from the
 * CURRENT level of an already-resolved hierarchy (see
 * `buildPieCompositionTree`) — i.e. either the root nodes or, after a
 * drill-down click, the clicked node's `children`. Returns `{ series: [] }`
 * when the config is incomplete (no category/value field) so the caller can
 * still render an (empty) chart safely.
 *
 * The legend uses ECharts' own native selection (see `buildLegend`) — same
 * as the Scatter & Bubble family — so a click just toggles that one entry's
 * slice independently of the others.
 */
export function buildPieCompositionOption(nodes: PcNode[], capabilities: PieCompositionCapabilities): EChartsOption {
  if (!capabilities?.categoryField || !capabilities?.valueField) return { series: [] };
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  if (!safeNodes.length) return { series: [] };

  const vf = capabilities.variantFlags;
  const isDonut = vf ? vf.donut === true : ['donut', 'semi-donut', 'nested-donut'].includes(capabilities.variant);
  const isSemiDonut = vf ? vf.semiDonut === true : capabilities.variant === 'semi-donut';
  const isNestedDonut = vf ? vf.nestedDonut === true : capabilities.variant === 'nested-donut';
  const isSunburst = vf ? vf.sunburst === true : capabilities.variant === 'sunburst';
  const isTreemap = vf ? vf.treemap === true : capabilities.variant === 'treemap';
  const isGauge = vf ? vf.gauge === true : ['radial-progress', 'circular-progress', 'ring-progress'].includes(capabilities.variant);
  const isRadialProgress = vf ? vf.radialProgress === true : capabilities.variant === 'radial-progress';
  const isRingProgress = vf ? vf.ringProgress === true : capabilities.variant === 'ring-progress';
  const progressStyle: 'radial' | 'circular' | 'ring' = isRadialProgress ? 'radial' : isRingProgress ? 'ring' : 'circular';

  const d = (capabilities.chartDefaults || {}) as unknown as Record<string, unknown>;
  const cfgPalette = (d['palette'] as string[])?.length ? (d['palette'] as string[]) : FALLBACK.palette;
  const palette = capabilities.paletteColors?.length ? capabilities.paletteColors : cfgPalette;
  const labelFontSize = capabilities.labelFontSize ?? (d['labelFontSize'] as number) ?? FALLBACK.labelFontSize;
  const labelColor = capabilities.labelColor || (d['labelColor'] as string) || FALLBACK.labelColor;
  const L = (d['layout'] as Record<string, number | undefined>) || {};
  const legendRightOffset = typeof L['legendRightOffset'] === 'number' ? (L['legendRightOffset'] as number) : FALLBACK.layout.legendRightOffset;
  const smallMultipleGap = typeof L['smallMultipleGap'] === 'number' ? (L['smallMultipleGap'] as number) : FALLBACK.layout.smallMultipleGap;
  const layout = { legendRightOffset };
  const label = labelConfig(capabilities, labelFontSize, labelColor);
  const legend = buildLegend(safeNodes.map((n) => n.name), capabilities, layout);
  const tooltip = buildTooltip(capabilities, capabilities.valueField);
  const animation = capabilities.animation !== false;
  const gradient: [string, string] = [FALLBACK.gradient[0], FALLBACK.gradient[1]];

  // ── Hierarchy: Sunburst / Treemap — full subtree beneath the current drill
  //    position renders in one shot (own click-drill re-roots one level at a
  //    time; native zoom/breadcrumb are disabled — see builder functions). ──
  if (isSunburst || isTreemap) {
    const sorted = applySortAndTopN(safeNodes, capabilities);
    const colorFn = buildColorResolver(sorted, capabilities, palette, gradient);
    const series = isSunburst ? buildSunburstSeries(sorted, colorFn) : buildTreemapSeries(sorted, colorFn, capabilities);
    return {
      color: palette,
      animation,
      tooltip: tooltip as unknown as EChartsOption['tooltip'],
      series: [series] as unknown as EChartsOption['series'],
    };
  }

  // ── Progress: Radial / Circular / Ring — one KPI meter, or N small
  //    multiples (one per top-level group) when Multiple Categories is on. ──
  if (isGauge) {
    const preset = resolveProgressPreset(capabilities, d, progressStyle);
    const trackColor = capabilities.trackColor || (d['trackColor'] as string) || FALLBACK.trackColor;
    const canSplit = capabilities.multipleCategories && safeNodes.some((n) => n.children?.length);
    if (canSplit) {
      const groups = safeNodes;
      const cells = smallMultiplesGrid(groups.length, smallMultipleGap);
      const colorFn = buildColorResolver(groups, capabilities, palette, gradient);
      const series = groups.map((g, i) => buildGaugeSeries(g, capabilities, preset, colorFn(g, i), trackColor, cells[i]?.center ?? ['50%', '50%'], cells[i]?.radius ?? '40%'));
      return { animation, tooltip: tooltip as unknown as EChartsOption['tooltip'], series: series as unknown as EChartsOption['series'] };
    }
    const total = safeNodes.reduce((s, n) => s + n.value, 0);
    const combined: PcNode = safeNodes.length === 1 ? safeNodes[0] : { name: capabilities.valueField, value: total };
    const series = buildGaugeSeries(combined, capabilities, preset, palette[0], trackColor, ['50%', '55%'], '75%');
    return { animation, tooltip: tooltip as unknown as EChartsOption['tooltip'], series: [series] as unknown as EChartsOption['series'] };
  }

  // ── Standard: Pie / Donut / Semi Donut / Nested Donut ──────────────────
  const geometry = resolvePieGeometry(capabilities, d, isDonut, isSemiDonut);

  if (isNestedDonut) {
    const sorted = applySortAndTopN(safeNodes, capabilities);
    const colorFn = buildColorResolver(sorted, capabilities, palette, gradient);
    const series = buildNestedDonutSeries(sorted, capabilities, palette, colorFn, d, label);
    return {
      color: palette,
      animation,
      tooltip: tooltip as unknown as EChartsOption['tooltip'],
      legend,
      series: series as unknown as EChartsOption['series'],
    };
  }

  const canSplitStandard = capabilities.multipleCategories && safeNodes.some((n) => n.children?.length);
  if (canSplitStandard) {
    const groups = safeNodes;
    const cells = smallMultiplesGrid(groups.length, smallMultipleGap);
    const series = groups.map((g, i) => {
      const children = applySortAndTopN(g.children?.length ? g.children : [{ name: g.name, value: g.value }], capabilities);
      const colorFn = buildColorResolver(children, capabilities, palette, gradient);
      return buildPieSeries(children, colorFn, geometry, label, cells[i]?.center ?? ['50%', '50%'], g.name);
    });
    return {
      color: palette,
      animation,
      tooltip: tooltip as unknown as EChartsOption['tooltip'],
      series: series as unknown as EChartsOption['series'],
    };
  }

  // Parent grouping without Multiple Categories: use each node's own total
  // (already summed over its children, if any) as the slice value.
  const flattened = safeNodes.map((n) => (n.children?.length ? { name: n.name, value: n.value, measure: n.measure } : n));
  const sorted = applySortAndTopN(flattened, capabilities);
  const colorFn = buildColorResolver(sorted, capabilities, palette, gradient);
  const series = buildPieSeries(sorted, colorFn, geometry, label);
  return {
    color: palette,
    animation,
    tooltip: tooltip as unknown as EChartsOption['tooltip'],
    legend,
    series: [series] as unknown as EChartsOption['series'],
  };
}
